import {
  AgentChatRequest,
  AgentResponse,
  AgentConfirmRequest,
  AgentContextResponse,
  AgentToolsResponse,
  AgentStatusResponse,
  ChatMessageDto,
  OnboardingResponse,
  FirstLoginResponse,
  OnboardingSetupResult,
  OnboardingAnalysisResponse,
  OnboardingQuestionsResponse,
  OnboardingShowcaseData,
  ClarifyingQuestion,
} from './types';

const AGENT_API_BASE = '/api/v1';

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorJson.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

async function fetchAgentStatus(): Promise<AgentStatusResponse> {
  const response = await fetch(`${AGENT_API_BASE}/agent/status`, {
    method: 'GET',
    credentials: 'include'
  });

  return handleResponse<AgentStatusResponse>(response);
}

export const agentService = {
  async chat(
    organizationId: string,
    message: string,
    conversationId?: string,
    history?: ChatMessageDto[],
    sessionKey?: string
  ): Promise<AgentResponse> {
    const request: AgentChatRequest = {
      organization: organizationId,
      message,
      conversationId,
      history,
      sessionKey
    };

    const response = await fetch(`${AGENT_API_BASE}/agent/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(request)
    });

    return handleResponse<AgentResponse>(response);
  },

  async confirm(stepId: string, confirmed: boolean, confirmationCode?: string): Promise<AgentResponse> {
    const request: AgentConfirmRequest = {
      stepId,
      confirmed,
      confirmationCode
    };

    const response = await fetch(`${AGENT_API_BASE}/agent/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(request)
    });

    return handleResponse<AgentResponse>(response);
  },

  async getContext(organizationId: string): Promise<AgentContextResponse> {
    const response = await fetch(`${AGENT_API_BASE}/organization/${organizationId}/agent/context`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    return handleResponse<AgentContextResponse>(response);
  },

  async getTools(organizationId: string): Promise<AgentToolsResponse> {
    const response = await fetch(`${AGENT_API_BASE}/organization/${organizationId}/agent/tools`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    return handleResponse<AgentToolsResponse>(response);
  },

  async getAgentStatus(): Promise<AgentStatusResponse> {
    return fetchAgentStatus();
  },

  async checkAgentAvailable(): Promise<boolean> {
    try {
      const status = await fetchAgentStatus();
      return status.enabled && status.agentEnabled !== false;
    } catch {
      return false;
    }
  },

  // Onboarding methods

  async createOnboardingContext(email: string, initialPrompt: string): Promise<OnboardingResponse> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, initialPrompt })
    });

    return handleResponse<OnboardingResponse>(response);
  },

  async getOnboardingContext(id: string): Promise<{ context: any } | { error: string }> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return handleResponse<{ context: any } | { error: string }>(response);
  },

  async linkUserToOnboarding(onboardingId: string): Promise<OnboardingResponse> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ onboardingId })
    });

    return handleResponse<OnboardingResponse>(response);
  },

  async runOnboardingSetup(onboardingId: string): Promise<OnboardingResponse> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/setup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ onboardingId })
    });

    return handleResponse<OnboardingResponse>(response);
  },

  async getFirstLoginContext(): Promise<FirstLoginResponse> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/first-login`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    return handleResponse<FirstLoginResponse>(response);
  },

  async completeOnboarding(onboardingId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ onboardingId })
    });

    return handleResponse<{ success: boolean }>(response);
  },

  // Enhanced onboarding methods

  async analyzeOnboarding(onboardingId: string): Promise<OnboardingResponse> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ onboardingId })
    });

    return handleResponse<OnboardingResponse>(response);
  },

  async getAnalysisStatus(onboardingId: string): Promise<OnboardingAnalysisResponse> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/analysis-status/${onboardingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return handleResponse<OnboardingAnalysisResponse>(response);
  },

  async buildOrganization(onboardingId: string): Promise<OnboardingResponse> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/build`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ onboardingId })
    });

    return handleResponse<OnboardingResponse>(response);
  },

  async getQuestions(onboardingId: string): Promise<OnboardingQuestionsResponse> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/questions/${onboardingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return handleResponse<OnboardingQuestionsResponse>(response);
  },

  async submitClarification(
    onboardingId: string,
    questionId: string,
    answer: string
  ): Promise<OnboardingResponse> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/clarify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ onboardingId, questionId, answer })
    });

    return handleResponse<OnboardingResponse>(response);
  },

  async getShowcaseData(onboardingId: string): Promise<{ data: OnboardingShowcaseData } | { error: string }> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/showcase/${onboardingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return handleResponse<{ data: OnboardingShowcaseData } | { error: string }>(response);
  },

  async submitCorrection(
    onboardingId: string,
    itemId: string,
    fieldPath: string,
    newValue: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${AGENT_API_BASE}/agent/onboarding/correct`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ onboardingId, itemId, fieldPath, newValue })
    });

    return handleResponse<{ success: boolean; message: string }>(response);
  }
};

export default agentService;
